import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { createContribution } from "../../../Api/Speakers/Contributions";

const AddContributionsModal = ({ show, onClose, onSaved, initialData }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    arabicOrganizationName: "",
    englishOrganizationName: "",
    participationType: "",
    arabicDescription: "",
    englishDescription: "",
    location: "",
    language: "",
    eventDate: "",
    eventTime: "",
    isOnline: null,
    contributeOn: "",
    imagePath: null,
    link: "",
    arabicTitle: "",
    englishTitle: "",
  });

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    toast.dismiss();

    const dateTime = new Date(formData.eventDate);
    if (formData.eventTime) {
      const [hours, minutes] = formData.eventTime.split(":");
      dateTime.setHours(parseInt(hours), parseInt(minutes));
    }

    const contributionData = {
      arabicTitle: formData.arabicTitle,
      englishTitle: formData.englishTitle,
      arabicOrganizationName: formData.arabicOrganizationName,
      englishOrganizationName:
        formData.englishOrganizationName || formData.arabicOrganizationName,
      arabicDescription: formData.arabicDescription,
      englishDescription: formData.englishDescription,
      contributionType: parseInt(formData.participationType),
      isOnline: formData.location === "online",
      language: parseInt(formData.language),
      contributeOn: dateTime.toISOString(),
      link: formData.link,
    };

    const submissionData = new FormData();
    Object.entries(contributionData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        submissionData.append(key, value);
      }
    });

    if (formData.imagePath) {
      submissionData.append("imagePath", formData.imagePath);
    }

    try {
      const isEdit = !!formData.id; 
      await createContribution(submissionData, formData.id); 

      toast.success(
        isEdit ? t("updated_successfully") : t("created_successfully")
      );
      onClose();
      onSaved?.();
    } catch (error) {
      const errors = error.response?.data?.errors;
      if (errors) {
        Object.values(errors)
          .flat()
          .forEach((msg) => toast.error(msg));
      } else {
        const fallbackMessage =
          error?.response?.data?.title ||
          error?.detail ||
          error?.message ||
          t("create_failed");

        toast.error(fallbackMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  

  useEffect(() => {
    if (initialData) {
      setFormData({
         id: initialData.id || "",
        arabicTitle: initialData.arabicTitle || "",
        englishTitle: initialData.englishTitle || "",
        arabicOrganizationName: initialData.arabicOrganizationName || "",
        englishOrganizationName: initialData.englishOrganizationName || "",
        participationType: initialData.contributionType?.toString() || "",
        arabicDescription: initialData.arabicDescription || "",
        englishDescription: initialData.englishDescription || "",
        location: initialData.isOnline ? "online" : "inPerson",
        language: initialData.language?.toString() || "",
        eventDate: initialData.contributeOn
          ? new Date(initialData.contributeOn).toISOString().split("T")[0]
          : "",
        eventTime: initialData.contributeOn
          ? new Date(initialData.contributeOn)
              .toISOString()
              .split("T")[1]
              ?.slice(0, 5)
          : "",
        isOnline: initialData.isOnline,
        contributeOn: initialData.contributeOn || "",
        imagePath: null, // لا نملأ الصورة
        link: initialData.link || "",
      });
    } else {
      setFormData({
        id: "",
        arabicTitle: "",
        englishTitle: "",
        arabicOrganizationName: "",
        englishOrganizationName: "",
        participationType: "",
        arabicDescription: "",
        englishDescription: "",
        location: "",
        language: "",
        eventDate: "",
        eventTime: "",
        isOnline: null,
        contributeOn: "",
        imagePath: null,
        link: "",
      });
    }
  }, [initialData]);

  

  return (
    <Modal show={show} onHide={onClose} backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("add_contribution")}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("arabic_title")}</Form.Label>
                <Form.Control
                  name="arabicTitle"
                  value={formData.arabicTitle}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("english_title")}</Form.Label>
                <Form.Control
                  name="englishTitle"
                  value={formData.englishTitle}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("arabic_organization")}</Form.Label>
                <Form.Control
                  name="arabicOrganizationName"
                  value={formData.arabicOrganizationName}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("english_organization")}</Form.Label>
                <Form.Control
                  name="englishOrganizationName"
                  value={formData.englishOrganizationName}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t("contribution_type")}</Form.Label>
            <Form.Select
              name="participationType"
              value={formData.participationType}
              onChange={handleChange}
            >
              <option value="">{t("select_type")}</option>
              <option value="1">{t("keynoteSpeech")}</option>
              <option value="2">{t("moderator")}</option>
              <option value="3">{t("lecture")}</option>
              <option value="4">{t("panelDiscussion")}</option>
              <option value="5">{t("workshop")}</option>
              <option value="6">{t("seminar")}</option>
            </Form.Select>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("date")}</Form.Label>
                <Form.Control
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("time")}</Form.Label>
                <Form.Control
                  type="time"
                  name="eventTime"
                  value={formData.eventTime}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t("venue")}</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="inPerson"
                name="location"
                label={t("inPerson")}
                value="inPerson"
                checked={formData.location === "inPerson"}
                onChange={handleChange}
              />
              <Form.Check
                type="radio"
                id="online"
                name="location"
                label={t("online")}
                value="online"
                checked={formData.location === "online"}
                onChange={handleChange}
              />
            </div>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("arabic_description")}</Form.Label>
                <Form.Control
                  as="textarea"
                  name="arabicDescription"
                  rows={3}
                  value={formData.arabicDescription}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t("english_description")}</Form.Label>
                <Form.Control
                  as="textarea"
                  name="englishDescription"
                  rows={3}
                  value={formData.englishDescription}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t("url")}</Form.Label>
            <Form.Control
              type="url"
              name="link"
              value={formData.link}
              onChange={handleChange}
              placeholder="https://example.com"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("image")}</Form.Label>
            <Form.Control
              type="file"
              name="imagePath"
              accept="image/*"
              onChange={handleChange}
            />
            {formData.imagePath && (
              <img
                src={URL.createObjectURL(formData.imagePath)}
                alt="Preview"
                style={{
                  maxHeight: "200px",
                  objectFit: "contain",
                  marginTop: "10px",
                }}
              />
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("language")}</Form.Label>
            <Form.Select
              name="language"
              value={formData.language}
              onChange={handleChange}
            >
              <option value="">{t("select_language")}</option>
              <option value="1">{t("arabic")}</option>
              <option value="2">{t("english")}</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("cancel")}
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? t("saving") + "..." : t("save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddContributionsModal;
